import type {ReactNode} from "react";
import {useReducedMotion} from "motion/react";

/**
 * Кнопки-действия лендинга: скачать для Android, создать анкету, войти, выбрать
 * тариф. Один компонент и один класс `rl-cta` — чтобы эффект не разошёлся по
 * разметке секций (Ирина: «единый эффект на все такие кнопки»).
 *
 * Что внутри (рисует `styles/landing.css`, значения — `./motion.ts`, LANDING_CTA):
 * ореол, блик, подъём при наведении, вдавливание при нажатии (общее
 * `usePressFeedback`), опускание иконки загрузки (`.rl-dl-icon`).
 *
 * Блик — отдельный декоративный `<span aria-hidden>` внутри кнопки: он режется
 * своей рамкой, а ореол кнопки остаётся снаружи нетронутым. Текст, `onClick`,
 * `href`, `target`, `rel` и атрибуты для аналитики проходят насквозь без
 * изменений. При «уменьшить движение» блика в разметке нет вовсе.
 *
 * `hero` — главная CTA (первый экран, финальный блок): блик идёт сам раз в
 * несколько секунд, ореол дышит. Без `hero` — только при наведении.
 */

interface CommonProps {
    /** Остальные классы кнопки: `rl-btn-brand rl-btn-lg` и т. п. */
    className?: string;
    hero?: boolean;
    children?: ReactNode;
}

function classes(className: string | undefined, hero: boolean | undefined): string {
    return ["rl-btn", "rl-cta", hero ? "rl-cta-hero" : "", className ?? ""].filter(Boolean).join(" ");
}

function Shine({reduceMotion}: {reduceMotion: boolean}) {
    if (reduceMotion) return null;
    return (
        <span className="rl-cta-shine" aria-hidden>
            <i/>
        </span>
    );
}

interface CtaButtonProps extends CommonProps {
    onClick: () => void;
}

export function CtaButton({className, hero, onClick, children}: CtaButtonProps) {
    const reduceMotion = Boolean(useReducedMotion());
    return (
        <button type="button" className={classes(className, hero)} onClick={onClick}>
            <Shine reduceMotion={reduceMotion}/>
            {children}
        </button>
    );
}

interface CtaLinkProps extends CommonProps {
    href: string;
}

/** Внешняя ссылка (скачивание APK): новая вкладка, как и раньше. */
export function CtaLink({className, hero, href, children}: CtaLinkProps) {
    const reduceMotion = Boolean(useReducedMotion());
    return (
        <a href={href} target="_blank" rel="noopener noreferrer" className={classes(className, hero)}>
            <Shine reduceMotion={reduceMotion}/>
            {children}
        </a>
    );
}
